import { BROADCAST_CHANNELS } from '~/constants/broadcastChannels';
import { logger } from '~/core/service/logger';

// Types for better type safety
type AuthBroadcastMessage = 'LOGIN' | 'LOGOUT';

// Singleton BroadcastChannel instance that persists for the lifetime of the app
let globalChannel: BroadcastChannel | null = null;
let isListenerInitialized = false;

function getGlobalChannel(): BroadcastChannel | null {
	if (import.meta.server) return null;

	if (!globalChannel) {
		try {
			globalChannel = new BroadcastChannel(BROADCAST_CHANNELS.AUTH_SYNC);
		} catch {
			console.warn('[AuthCrossTabSync] BroadcastChannel not supported');
			return null;
		}
	}
	return globalChannel;
}

/**
 * Composable for synchronizing authentication state across browser tabs.
 * When a user logs in or out in one tab, other tabs are notified and update their state accordingly.
 *
 * Uses a singleton BroadcastChannel to avoid issues with channel being closed prematurely.
 */
export function useAuthCrossTabSync() {
	const { fetch: fetchSession, clear: clearSession } = useUserSession();

	// Initialize the listener only once (in app.vue)
	if (import.meta.client && !isListenerInitialized) {
		const channel = getGlobalChannel();

		if (channel) {
			isListenerInitialized = true;

			channel.onmessage = async (event: MessageEvent<AuthBroadcastMessage>) => {
				const message = event.data;

				try {
					if (message === 'LOGIN') {
						await fetchSession();
					} else if (message === 'LOGOUT') {
						// First clear the client-side state immediately for responsive UI
						await clearSession();
						// Then refresh from server to ensure sync (cookies already cleared by initiating tab)
						await fetchSession();
					}
				} catch (error) {
					logger.error({
						source: 'useAuthCrossTabSync',
						message: 'Failed to sync auth state across tabs',
						originalErrorObject: error
					});
				}
			};
		}
	}

	/**
	 * Notify other browser tabs that the user has logged in.
	 * This triggers session refresh in other tabs to sync the authentication state.
	 */
	function notifyLogin() {
		const channel = getGlobalChannel();
		if (channel) {
			channel.postMessage('LOGIN');
		}
	}

	/**
	 * Notify other browser tabs that the user has logged out.
	 * This triggers session clearing in other tabs to sync the authentication state.
	 */
	function notifyLogout() {
		const channel = getGlobalChannel();
		if (channel) {
			channel.postMessage('LOGOUT');
		}
	}

	return {
		notifyLogin,
		notifyLogout
	};
}
